import { useState } from 'react';
import { Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Input } from '@atoms/Input';
import { searchService } from '@services/searchService';
import type { Product, User } from '@/types';

interface SearchBarProps {
  className?: string;
  placeholder?: string;
}

export function SearchBar({ className, placeholder = 'Ürün veya toptancı ara...' }: SearchBarProps) {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const navigate = useNavigate();

  const { data, isFetching } = useQuery({
    queryKey: ['search', query],
    queryFn: () => searchService.search(query),
    enabled: query.length >= 2,
  });

  const showResults = focused && query.length >= 2;

  const goSearchPage = () => {
    if (query.trim().length >= 2) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
    }
  };

  return (
    <div className={clsx('relative', className)}>
      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 200)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            goSearchPage();
          }
        }}
        placeholder={placeholder}
        className="rounded-xl border-slate-200 bg-slate-50 pr-10 focus:bg-white"
      />
      <button
        type="button"
        aria-label="Ara"
        onClick={goSearchPage}
        className="absolute right-3 top-2.5 text-slate-400 transition hover:text-slate-600"
      >
        <Search className="h-4 w-4" />
      </button>

      {showResults && (
        <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-card-hover">
          {isFetching ? (
            <p className="p-4 text-sm text-slate-500">Aranıyor...</p>
          ) : (
            <>
              {data?.products?.length ? (
                <div className="border-b border-slate-100 p-2">
                  <p className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Ürünler</p>
                  {data.products.slice(0, 5).map((p: Product) => (
                    <button
                      key={p.id}
                      type="button"
                      className="block w-full rounded-lg px-2 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                      onMouseDown={() => navigate(`/restaurant?tab=browse&wholesaler=${p.wholesalerId}`)}
                    >
                      {p.name}
                    </button>
                  ))}
                </div>
              ) : null}
              {data?.wholesalers?.length ? (
                <div className="p-2">
                  <p className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Toptancılar</p>
                  {data.wholesalers.slice(0, 5).map((w: User) => (
                    <button
                      key={w.id}
                      type="button"
                      className="block w-full rounded-lg px-2 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                      onMouseDown={() => navigate(`/restaurant?tab=browse&wholesaler=${w.id}`)}
                    >
                      {w.name}
                    </button>
                  ))}
                </div>
              ) : null}
              {!data?.products?.length && !data?.wholesalers?.length && (
                <p className="p-4 text-sm text-slate-500">Sonuç bulunamadı</p>
              )}
              {(data?.products?.length || data?.wholesalers?.length) ? (
                <button
                  type="button"
                  className="w-full border-t border-slate-100 px-4 py-2.5 text-sm font-semibold text-brand-600 hover:bg-slate-50"
                  onMouseDown={goSearchPage}
                >
                  Tüm sonuçları gör →
                </button>
              ) : null}
            </>
          )}
        </div>
      )}
    </div>
  );
}
